import { create } from 'zustand';

import {
  deleteSavedSection as deleteSavedSectionApi,
  fetchSavedSections,
} from '@/lib/saved-sections/api';
import type { SavedSection } from '@/types/saved-section';

interface SavedSectionsState {
  savedSections: SavedSection[];
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  pendingOpenRequest: boolean;
  fetchSavedSections: () => Promise<void>;
  addSavedSection: (section: SavedSection) => void;
  removeSavedSection: (id: string) => void;
  deleteSavedSection: (id: string) => Promise<void>;
  requestOpen: () => void;
  consumeOpenRequest: () => void;
  reset: () => void;
}

export const useSavedSectionsStore = create<SavedSectionsState>((set, get) => ({
  savedSections: [],
  isLoaded: false,
  isLoading: false,
  error: null,
  pendingOpenRequest: false,

  fetchSavedSections: async () => {
    if (get().isLoaded || get().isLoading) return;

    set({ isLoading: true, error: null });
    try {
      const sections = await fetchSavedSections();
      set({ savedSections: sections, isLoaded: true, isLoading: false });
    } catch {
      set({ error: 'Failed to load saved sections.', isLoading: false });
    }
  },

  addSavedSection: (section) => {
    set((state) => ({ savedSections: [section, ...state.savedSections] }));
  },

  removeSavedSection: (id) => {
    set((state) => ({ savedSections: state.savedSections.filter((s) => s.id !== id) }));
  },

  deleteSavedSection: async (id: string) => {
    await deleteSavedSectionApi(id);
    get().removeSavedSection(id);
  },

  requestOpen: () => set({ pendingOpenRequest: true }),

  consumeOpenRequest: () => set({ pendingOpenRequest: false }),

  reset: () =>
    set({
      savedSections: [],
      isLoaded: false,
      isLoading: false,
      error: null,
      pendingOpenRequest: false,
    }),
}));
